import { BillitInvoice, BillitPurchaseOrderLink, PurchaseOrder } from './types';

/** Coerce API/Prisma amounts (number, string, Decimal) to a finite number or null. */
export const coerceAmount = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.round(value * 100) / 100 : null;
  }
  const raw = String(value).trim().replace(/\s/g, '');
  if (!raw) return null;
  const normalized = raw.includes(',') && raw.includes('.')
    ? raw.replace(/\./g, '').replace(',', '.')
    : raw.replace(',', '.');
  const num = Number(normalized);
  if (!Number.isFinite(num) || num < 0) return null;
  return Math.round(num * 100) / 100;
};

export const parseLinkAmountInput = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return coerceAmount(trimmed);
};

export const formatAmountForInput = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return '';
  return String(Math.round(value * 100) / 100);
};

export const formatBillitMoney = (
  value: number | string | null | undefined,
  currency = 'EUR',
): string => {
  const amount = coerceAmount(value);
  if (amount == null) return '—';
  return `${amount.toFixed(2)} ${currency}`;
};

export const getLinkLinkedAmount = (link: BillitPurchaseOrderLink): number | null =>
  coerceAmount(link.linked_amount);

export const sumActiveLinkedAmounts = (links: BillitPurchaseOrderLink[]): number =>
  links.reduce((sum, link) => sum + (getLinkLinkedAmount(link) ?? 0), 0);

export const getInvoiceAmountExcl = (invoice: BillitInvoice): number | null =>
  coerceAmount(invoice.total_excl);

export const getInvoiceAmountIncl = (invoice: BillitInvoice): number | null =>
  coerceAmount(invoice.total_incl);

export const getPurchaseOrderSuggestedAmount = (
  po: PurchaseOrder | undefined,
): number | null => coerceAmount(po?.total_excl);

export const normalizePurchaseOrderLinks = (
  links: BillitPurchaseOrderLink[] | undefined,
): BillitPurchaseOrderLink[] =>
  (links || []).map(link => ({
    ...link,
    linked_amount: getLinkLinkedAmount(link),
  }));

export const normalizeBillitInvoice = (invoice: BillitInvoice): BillitInvoice => ({
  ...invoice,
  total_excl: getInvoiceAmountExcl(invoice),
  total_incl: getInvoiceAmountIncl(invoice),
  purchase_order_links: normalizePurchaseOrderLinks(invoice.purchase_order_links),
});

export const getRemainingInvoiceExcl = (
  invoice: BillitInvoice,
  activeLinks: BillitPurchaseOrderLink[],
): number | null => {
  const invoiceTotal = getInvoiceAmountExcl(invoice);
  if (invoiceTotal == null) return null;
  return Math.round((invoiceTotal - sumActiveLinkedAmounts(activeLinks)) * 100) / 100;
};
